import { useState, useEffect, useRef } from "react";
import { useClock } from "./useClock";

interface ClockTime {
  hour: string;
  canSendOrders: boolean;
}

export const useClockTime = (): ClockTime => {
  const { data: clockData } = useClock();
  const [time, setTime] = useState<ClockTime>({
    hour: "--:--:--",
    canSendOrders: false,
  });

  const dateClockRef = useRef<Date | null>(null);
  const dateControlRef = useRef<Date | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Inicializa e atualiza o relógio quando os dados são carregados ou atualizados
  useEffect(() => {
    if (clockData?.config?.atual) {
      // Atualiza imediatamente quando os dados chegam
      dateClockRef.current = new Date(clockData.config.atual * 1000);
      dateControlRef.current = new Date();

      // Atualiza o estado imediatamente também, sem esperar o próximo ciclo
      const hours = `0${dateClockRef.current.getHours()}`;
      const minutes = `0${dateClockRef.current.getMinutes()}`;
      const seconds = `0${dateClockRef.current.getSeconds()}`;
      const formattedTime = `${hours.slice(-2)}:${minutes.slice(-2)}:${seconds.slice(-2)}`;

      // Verifica se pode enviar pedidos
      const closingTime = clockData.config.max.split(":");
      const closingLimit = new Date();
      const hour = closingTime[0];
      const minute = closingTime[1];
      if (hour && minute) {
        closingLimit.setHours(parseInt(hour, 10));
        closingLimit.setMinutes(parseInt(minute, 10));
      }
      closingLimit.setSeconds(0);

      const canSendOrders = new Date() < closingLimit;

      setTime({
        hour: formattedTime,
        canSendOrders,
      });
    }
  }, [clockData?.config?.atual, clockData?.config?.max]);

  useEffect(() => {
    // Limpa o intervalo anterior se existir
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Só inicia o relógio se temos os dados necessários
    if (
      !clockData?.config?.atual ||
      !clockData?.config?.max ||
      !dateClockRef.current
    ) {
      return;
    }

    // Função recursiva usando setTimeout para maior precisão
    const updateClock = () => {
      if (!dateClockRef.current || !dateControlRef.current) {
        return;
      }

      const actualDate = new Date();

      const diferenceBetweenActualControl =
        actualDate.getTime() - dateControlRef.current.getTime();

      if (dateClockRef.current) {
        dateClockRef.current.setTime(
          dateClockRef.current.getTime() + diferenceBetweenActualControl
        );
      }

      dateControlRef.current = actualDate;

      // Formata o horário
      if (!dateClockRef.current) return;

      const hours = `0${dateClockRef.current.getHours()}`;
      const minutes = `0${dateClockRef.current.getMinutes()}`;
      const seconds = `0${dateClockRef.current.getSeconds()}`;
      const formattedTime = `${hours.slice(-2)}:${minutes.slice(-2)}:${seconds.slice(-2)}`;

      // Verifica se pode enviar pedidos
      const closingTime = clockData.config.max.split(":");
      const closingLimit = new Date();
      const hour = closingTime[0];
      const minute = closingTime[1];
      if (hour && minute) {
        closingLimit.setHours(parseInt(hour, 10));
        closingLimit.setMinutes(parseInt(minute, 10));
      }
      closingLimit.setSeconds(0);

      const canSendOrders = actualDate < closingLimit;

      setTime({
        hour: formattedTime,
        canSendOrders,
      });

      // Agenda a próxima atualização com setTimeout para maior precisão
      // Calcula o delay para compensar o tempo de execução e sincronizar com o segundo exato
      const now = Date.now();
      const nextSecond = Math.ceil(now / 1000) * 1000;
      const delay = nextSecond - now;
      intervalRef.current = setTimeout(
        updateClock,
        delay || 1000
      ) as unknown as NodeJS.Timeout;
    };

    // Inicia o relógio imediatamente se já temos os dados
    if (dateClockRef.current && dateControlRef.current) {
      updateClock();
    }

    // Limpa o timeout quando o componente é desmontado ou os dados mudam
    return () => {
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
      }
    };
  }, [clockData]);

  return time;
};
